import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchEmployer360, type Employer360 } from '../lib/userDetailService';
import { disableAccount, resetPassword } from '../lib/userService';
import { logger } from '../lib/logger';

const statusLabels: Record<string, string> = {
  active: 'Actif',
  pending_verification: 'En attente',
  suspended: 'Suspendu',
  rejected: 'Rejeté',
};
const statusClasses: Record<string, string> = {
  active: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_verification: 'bg-amber-50 text-amber-700 border-amber-200',
  suspended: 'bg-red-50 text-red-700 border-red-200',
};
const offerStatusLabels: Record<string, string> = {
  draft: 'Brouillon', active: 'Active', paused: 'En pause', closed: 'Fermée', expired: 'Expirée',
};
const videoStatusLabels: Record<string, string> = {
  uploading: 'Envoi', analyzing: 'Analyse IA', pending_review: 'À modérer',
  approved: 'Approuvée', rejected: 'Rejetée', ready: 'Prête',
};

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
      <p className="text-sm text-gray-800 mt-0.5">{value || '—'}</p>
    </div>
  );
}

const EmployerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [e, setE] = useState<Employer360 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');

  useEffect(() => {
    if (!id) return;
    fetchEmployer360(id)
      .then(setE)
      .catch((err) => {
        logger.error('load_employer_360', 'EmployerDetail', { error: String(err) });
        setError('Recruteur introuvable');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const handleToggleSuspend = async () => {
    if (!e) return;
    setActionLoading(true);
    try {
      const willSuspend = e.status !== 'suspended';
      await disableAccount(e.userId, willSuspend);
      setE({ ...e, status: willSuspend ? 'suspended' : 'active' });
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPwd = async () => {
    if (!e || newPwd.length < 6) return;
    setActionLoading(true);
    try {
      await resetPassword(e.userId, newPwd);
      alert('Mot de passe réinitialisé.');
      setResetOpen(false);
      setNewPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !e) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 font-medium mb-4">{error || 'Erreur'}</p>
        <button onClick={() => navigate('/employers')} className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] rounded-lg">
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* En-tête */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
        <div className="w-16 h-16 rounded-xl bg-[#365A8C] flex items-center justify-center text-white text-xl font-bold">
          🏢
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">{e.companyName || '—'}</h2>
            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusClasses[e.status] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
              {statusLabels[e.status] || e.status}
            </span>
            {e.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
            {e.subscriptionActive ? (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Abonné</span>
            ) : (
              <span className="px-2 py-0.5 text-[11px] rounded-full bg-gray-50 text-gray-500 border border-gray-200">Sans abonnement</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Contact : {e.firstName} {e.lastName}{e.positionInCompany ? ` (${e.positionInCompany})` : ''}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
            <span>✉️ {e.email || '—'}</span>
            <span>📞 {e.phone || '—'}</span>
            <span>📍 {[e.address, e.zipcode, e.city].filter(Boolean).join(', ') || '—'}</span>
            <span>Inscrit le {e.registeredAt}</span>
            <span>Dernière connexion : {e.lastLoginAt || 'jamais'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleToggleSuspend}
            disabled={actionLoading}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              e.status === 'suspended' ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
            } disabled:opacity-40`}
          >
            {e.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
          </button>
          <button
            onClick={() => { setResetOpen(true); setNewPwd(''); }}
            className="px-3 py-1.5 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md"
          >
            Réinit. mdp
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Candidatures reçues', value: e.stats.applicationsReceived, icon: '📩' },
          { label: 'Profils consultés', value: e.stats.profileViewsMade, icon: '👁️' },
          { label: 'Favoris', value: e.stats.favoritesMade, icon: '⭐' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
            <span className="text-2xl">{s.icon}</span>
            <div>
              <p className="text-xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Entreprise */}
      <Card title="Entreprise">
        <div className="grid grid-cols-4 gap-x-4 gap-y-3">
          <Field label="SIRET" value={e.siret ? `${e.siret}${e.siretVerified ? ' ✓' : ' (non vérifié)'}` : null} />
          <Field label="Effectif" value={e.employeeCount} />
          <Field label="Profil complet" value={e.profileComplete ? 'Oui' : 'Non'} />
          <Field label="Client Stripe" value={e.stripeCustomerId} />
        </div>
      </Card>

      {/* Offres */}
      <Card title={`Offres (${e.offers.length})`}>
        {e.offers.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune offre</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="pb-2">Poste</th>
                <th className="pb-2">Établissement</th>
                <th className="pb-2">Ville</th>
                <th className="pb-2">Contrat</th>
                <th className="pb-2">Statut</th>
                <th className="pb-2">Candidatures</th>
                <th className="pb-2">Créée le</th>
              </tr>
            </thead>
            <tbody>
              {e.offers.map((o) => (
                <tr key={o.id} className="border-t border-gray-100">
                  <td className="py-2 font-medium text-gray-800">{o.positionName}</td>
                  <td className="py-2 text-gray-600">{o.establishmentName}</td>
                  <td className="py-2 text-gray-600">{o.city}</td>
                  <td className="py-2 text-gray-600 uppercase text-xs">{o.contractType || '—'}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${o.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {offerStatusLabels[o.status] || o.status}
                    </span>
                  </td>
                  <td className="py-2 text-gray-600">{o.applicationsCount}</td>
                  <td className="py-2 text-gray-500">{o.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Vidéos */}
      <Card title={`Vidéos (${e.videos.length})`}>
        {e.videos.length === 0 ? (
          <p className="text-sm text-gray-400">Aucune vidéo</p>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {e.videos.map((v) => (
              <div key={v.id} className="w-28">
                <div className="w-28 h-40 rounded-lg bg-gray-100 overflow-hidden relative">
                  {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute bottom-1 left-1 right-1 text-center px-1 py-0.5 text-[10px] font-medium rounded bg-black/60 text-white">
                    {videoStatusLabels[v.status] || v.status}
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-center truncate">{v.title || v.createdAt}</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal reset password */}
      {resetOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={(ev) => ev.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{e.firstName} {e.lastName} ({e.email})</p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={newPwd}
                onChange={(ev) => setNewPwd(ev.target.value)}
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setResetOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={newPwd.length < 6 || actionLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerDetail;
